import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import "../styles/NewsCard.css";
import AdSenseAd from "./AdSenseAd";
import {
  FaFacebook,
  FaWhatsapp,
  FaThumbsUp,
  FaRegThumbsUp,
  FaTimes,
  FaComment,
} from "react-icons/fa";
import { IoIosShareAlt } from "react-icons/io";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/main.css";
import GoogleLoginButton from "./GoogleLoginButton";
import { useAuth } from "../context/AuthContext";

const NewsCard = ({ article, featured = false, showAd = false, isAdContainer = false }) => {
  // const commentSectionRef = useRef(null);
  const { id } = useParams();
  const [showShareOptions, setShowShareOptions] = useState(false);
  // const [authMode, setAuthMode] = useState("choice");
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [loadingLike, setLoadingLike] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { googleLogin, currentUser } = useAuth();

  const fetchComments = async () => {
    try {
      setCommentLoading(true);
      const response = await fetch(
        `https://tnreaders.mpdatahub.com/mobile/comments/${article.id || article._id}`
      );

      if (response.ok) {
        const data = await response.json();
        setComments(Array.isArray(data) ? data : []);
      } else {
        setComments([]); 
      }
    } catch (err) {
      setCommentError("Failed to load comments");
      console.error("Error fetching comments:", err);
      setComments([]);
    } finally {
      setCommentLoading(false);
    }
  };

  useEffect(() => {
    if (article && (article.id || article._id)) {
      fetchComments();
    }
  }, [article]);

  useEffect(() => {
    const checkAuthStatus = () => {
      const userData = localStorage.getItem("user");
      return !!userData;
    };

    if (checkAuthStatus() && showLoginModal) {
      setShowLoginModal(false);
    }
  }, [currentUser, showLoginModal]);

  useEffect(() => {
    const fetchLikeStatus = async () => {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (!storedUser) {
        setLiked(false);
        setLikesCount(0);
        return;
      }
      try {
        setLiked(false);
        setLikesCount(0);
      } catch (error) {
        console.error("Error fetching like status:", error);
      }
    };

    if (article && (article.id || article._id)) {
      fetchLikeStatus();
    }
  }, [article, currentUser]);

  const dateToFormat = article.createdAt || article.publishedAt || article.date;
  const formattedDate = dateToFormat
    ? format(new Date(dateToFormat), "MMMM d, yyyy")
    : "";

  if (article.status === false) return null;

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !article) return;

    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) {
      setShowLoginModal(true);
      return;
    }

    const userName = storedUser?.name || storedUser?.displayName || "Anonymous";

    try {
      setCommentLoading(true);
      const articleId = article.id || article._id;
      const response = await fetch(
        "https://tnreaders.mpdatahub.com/mobile/comments-store",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            article_metadata_id: articleId,
            comment_name: userName,
            comment_text: newComment,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to post comment");
      }

      const savedComment = await response.json();
      const newCommentObj = {
        id: savedComment.id || Date.now(),
        comment_name: userName,
        comment_text: newComment,
        created_at: new Date().toISOString(),
        replies: []
      };
      
      setComments([...comments, newCommentObj]);
      setNewComment("");
      setCommentError(null);
      setShowCommentForm(false);
      fetchComments();
    } catch (err) {
      setCommentError(err.message);
      alert(err.message);
      console.error("Comment submission error:", err);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleClick = () => {
    if (isAdContainer) return;

    const articleElement = document.getElementById(`article-${article._id || article.id}`);
    const scrollPosition = articleElement
      ? articleElement.getBoundingClientRect().top + window.scrollY
      : window.scrollY;

    navigate(`/article/${article.id || article._id}`, {
      state: {
        article,
        fromApp: true,
        currentCategory: article.category,
        scrollPosition,
        returnPath: `/${article.category}`,
        articleId: article.id || article._id,
      },
    });
  };

  const toggleShareOptions = (e) => {
    e.stopPropagation();
    setShowShareOptions(!showShareOptions);
  };

  const shareViaWhatsApp = async (e, groupId = null) => {
    e.stopPropagation();

    const articleUrl = `${window.location.origin}/article/${article.id || article._id}`;
    const shareText = `🔷 *${article.title}* 🔷\n\n${article.summary || article.content || ""
      }\n\n${articleUrl}`;

    try {
      if (navigator.share && navigator.canShare) {
        const shareData = {
          title: article.title || "TodayTalks Article",
          text: shareText,
          url: articleUrl,
        };
        await navigator.share(shareData);
        setShowShareOptions(false);
        return;
      }
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
    } catch (error) {
      console.error("Error sharing:", error);
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
    }
    setShowShareOptions(false);
  };

  const shareOnSocialMedia = (platform, e) => {
    e.stopPropagation();
    const articleUrl = `${window.location.origin}/article/${article.id || article._id}`;
    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            articleUrl
          )}`,
          "_blank"
        );
        break;
      case "whatsapp":
        shareViaWhatsApp(e); 
        break;
      case "whatsapp-group":
        shareViaWhatsApp(e, "1234567890-123456@g.us"); 
        break;
      default:
        break;
    }

    setShowShareOptions(false);
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) {
      setShowLoginModal(true);
      return;
    }
    const userId = storedUser?.id;
    if (!userId) {
      setShowLoginModal(true);
      return;
    }
    setLoadingLike(true);
    try {
      const articleId = article.id || article._id;
      const res = await fetch(
        `https://tnreaders.mpdatahub.com/mobile/Content/like`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: userId,
            category: article.category || "Updates", 
            id: articleId
          }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        setLiked(!liked);
        setLikesCount(prev => liked ? prev - 1 : prev + 1);
      } else {
        throw new Error("Failed to like article");
      }
    } catch (error) {
      console.error("Error liking article:", error);
      alert("Failed to like article. Please try again.");
    } finally {
      setLoadingLike(false);
    }
  };

  const handleGoogleSuccess = async (credential) => {
    try {
      setError("");
      setLoading(true);
      const result = await googleLogin(credential);
      if (result.success) {
        setTimeout(() => {
          handleLike({ stopPropagation: () => { } });
        }, 500);
      } else {
        setError(result.message || "Google authentication failed");
      }
    } catch (error) {
      setError("Google authentication error");
      console.error("Google auth error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = (errorMessage) => {
    setError(errorMessage || "Google sign-in failed. Please try again.");
  };

  const handleEmailLogin = () => {
    setShowLoginModal(false);
    navigate("/login", {
      state: {
        returnTo: window.location.pathname,
        articleId: article.id || article._id,
      },
    });
  };

  if (isAdContainer) {
    return (
      <div className="ad-container-standalone">
        <div className="ad-label">Advertisement</div>
        <AdSenseAd
          slot="1543185295"
          format="display"
          style={{ display: "block", width: "100%", height: "250px" }}
        />
      </div>
    );
  }

  return (
    <>
      <div
        className={`news-card21 ${featured ? "featured" : ""}`}
        onClick={handleClick}
        style={{
          cursor: "pointer",
          position: "relative",
          backgroundColor: "var(--card-bg)",
          boxShadow: "var(--box-shadow) 0px 2px 8px 0px",
          borderRadius: "7px",
        }}
      >
        <div className="card-image">
          {article.imageUrl && (
            <img
              src={article.imageUrl}
              alt={article.title}
              onError={(e) => {
                e.target.src = "/images/icon.png";
              }}
            />
          )}
        </div>
        <div className="card-content">
          <h3 className="card-title">{article.title}</h3>
          <div className="card-footer">
            {formattedDate && <span className="date">{formattedDate}</span>}
            {article.trending && <span className="trending">🔥 Trending</span>}
            {article.category && (
              <span className="category-tag">{article.category}</span>
            )}
          </div>
          {/* <div
            className="card-actions"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              alignItems: "center",
              marginTop: "20px",
              padding: "0 0px",
            }}
          >
            <button
              className="like-buttonnews"
              onClick={handleLike}
              disabled={loadingLike}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "22px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <motion.span
                key={liked}
                initial={{ scale: 0.8 }}
                animate={{ scale: 1.2 }}
                transition={{ type: "spring", stiffness: 300, damping: 10 }}
              >
                {liked ? (
                  <FaThumbsUp style={{ color: "#0d6efd" }} />
                ) : (
                  <FaRegThumbsUp style={{ color: "gray" }} />
                )}
              </motion.span>
              <span>{likesCount}</span>
            </button>
            <button
              className="comment-button"
              onClick={(e) => {
                e.stopPropagation();
                const storedUser = JSON.parse(localStorage.getItem("user"));
                if (storedUser) {
                  setShowCommentForm(!showCommentForm);
                } else {
                  setShowLoginModal(true);
                }
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "22px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <FaComment />
              <span>{comments.length}</span>
            </button>
            <div
              className="share-buttonnews"
              onClick={toggleShareOptions}
              style={{
                cursor: "pointer",
                fontSize: "22px",
              }}
            >
              <IoIosShareAlt />
            </div>
          </div> */}
          <AnimatePresence>
            {showShareOptions && (
              <motion.div
                className="share-optionsnews"
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: "absolute",
                  right: "0",
                  background: "white",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  padding: "10px",
                  zIndex: 100,
                  minWidth: "140px"
                }}
              >
                <div className="share-options-contentnews" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div
                    onClick={(e) => shareOnSocialMedia("facebook", e)}
                    className="share-optionnews"
                    style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px", borderRadius: "6px", cursor: "pointer" }}
                  >
                    <FaFacebook style={{ color: "#3b5998", fontSize: "20px" }} />
                    <span>Facebook</span>
                  </div>

                  <div
                    onClick={(e) => shareOnSocialMedia("whatsapp", e)}
                    className="share-optionnews"
                    style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px", borderRadius: "6px", cursor: "pointer" }}
                  >
                    <FaWhatsapp style={{ color: "#25D366", fontSize: "20px" }} />
                    <span>WhatsApp</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {showCommentForm && (
        <form onSubmit={handleCommentSubmit} className="comment-form">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Share your thoughts..."
            rows="3"
            disabled={commentLoading}
            required
          />
          <div className="comment-form-buttons">
            <button
              type="submit"
              disabled={commentLoading || !newComment.trim()}
            >
              {commentLoading ? "Posting..." : "Post Comment"}
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={() => setShowCommentForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      <AnimatePresence>
        {showLoginModal && (
          <>
            <motion.div
              className="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-end",
                zIndex: 1000,
              }}
              onClick={() => setShowLoginModal(false)}
            >
              <motion.div
                className="modal-content"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                style={{
                  backgroundColor: "#1e293b",
                  padding: "20px",
                  borderTopLeftRadius: "6px",
                  borderTopRightRadius: "6px",
                  width: "100%",
                  maxWidth: "500px",
                  position: "relative",
                  maxHeight: "80vh",
                  overflowY: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowLoginModal(false)}
                  style={{
                    position: "absolute",
                    top: "15px",
                    right: "15px",
                    background: "none",
                    border: "none",
                    fontSize: "20px",
                    cursor: "pointer",
                    color: "white",
                  }}
                >
                  <FaTimes />
                </button>
                <h3
                  style={{
                    marginBottom: "15px",
                    textAlign: "center",
                    color: "white",
                    fontSize: "1.2rem",
                  }}
                >
                  Login to continue
                </h3>
                {error && (
                  <div
                    className="error-message"
                    style={{
                      color: "#ff6b6b",
                      marginBottom: "15px",
                      textAlign: "center",
                      padding: "8px",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  >
                    {error}
                  </div>
                )}
                <div className="oauth-section">
                  <GoogleLoginButton
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="Login with Google"
                  />
                  <div
                    className="divider"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "15px 0",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        height: "1px",
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                      }}
                    ></div>
                    <span
                      style={{
                        padding: "0 10px",
                        color: "rgba(255, 255, 255, 0.7)",
                      }}
                    >
                      or
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: "1px",
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                      }}
                    ></div>
                  </div>
                  <button
                    onClick={handleEmailLogin}
                    style={{
                      width: "100%",
                      padding: "12px",
                      color: "rgb(5, 65, 69)",
                      backgroundColor: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "500",
                      marginBottom: "10px",
                    }}
                  >
                    Use Email & Password
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NewsCard;